import React, { useState, useEffect, useRef } from 'react';
import './Notification.css';
import { usePagination } from '../../hooks/usePagination';
import { getNotificationLogs, NotificationLogResponse } from '../../Services/serviceURL';

// Interface for transformed notification data for display
interface NotificationDisplayData {
    id: string;
    date: string;
    notification: string;
    type: string;
    status: string;
    alarmCount: number;
    recipients: string[];
    content: string;
    rawData: NotificationLogResponse;
}

// Helper functions to transform API data
const getDeliveryStatusText = (status: number): string => {
    switch (status) {
        case 0:
            return 'Sent';
        case 1:
            return 'Failed';
        default:
            return 'Failed'; // Default all other statuses to Failed
    }
};

const getDeliveryFormatText = (format: number): string => {
    return 'Email'; // Only Email type supported
};

const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
    }).replace(',', '');
};

const transformNotificationData = (apiData: NotificationLogResponse[]): NotificationDisplayData[] => {
    return apiData.map(item => ({
        id: item.id,
        date: formatDateTime(item.notificationSentAt),
        notification: `Daily Alarm Notification (${item.alarmCount} alert${item.alarmCount !== 1 ? 's' : ''})`,
        type: getDeliveryFormatText(item.deliveryFormat),
        status: getDeliveryStatusText(item.deliveryStatus),
        alarmCount: item.alarmCount,
        recipients: item.recipients,
        content: item.content,
        rawData: item
    }));
};

const Notification: React.FC = () => {
    const [data, setData] = useState<NotificationDisplayData[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedNotification, setSelectedNotification] = useState<NotificationDisplayData | null>(null);
    const dialogRef = useRef<HTMLDialogElement>(null);
    const itemsPerPage = 10;

    // Fetch notification logs from API
    useEffect(() => {
        const fetchNotificationLogs = async () => {
            try {
                setLoading(true);
                setError(null);
                const apiData = await getNotificationLogs();
                const transformedData = transformNotificationData(apiData);
                setData(transformedData);
            } catch (err) {
                console.error('Failed to fetch notification logs:', err);
                setError('Failed to load notification logs. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        fetchNotificationLogs();
    }, []);

    // Filter data based on search and status
    const filteredData = data.filter(item => {
        const matchesSearch = item.notification.toLowerCase().includes(searchText.toLowerCase()) ||
                             item.type.toLowerCase().includes(searchText.toLowerCase());
        const matchesStatus = statusFilter === 'All' || item.status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    const {
        currentPage,
        totalPages,
        currentData,
        totalItems,
        goToPreviousPage,
        goToNextPage
    } = usePagination({ data: filteredData, itemsPerPage });

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(e.target.value);
    };

    const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setStatusFilter(e.target.value);
    };

    const handleViewContent = (notification: NotificationDisplayData) => {
        setSelectedNotification(notification);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedNotification(null);
    };

    const handleDialogCancel = (e: React.SyntheticEvent<HTMLDialogElement>) => {
        e.preventDefault();
        closeModal();
    };

    const handleDialogClick = (e: React.MouseEvent<HTMLDialogElement>) => {
        if (e.target === e.currentTarget) {
            closeModal();
        }
    };

    useEffect(() => {
        if (!isModalOpen) return;

        const dialog = dialogRef.current;
        dialog?.showModal();

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            dialog?.close();
            document.body.style.overflow = previousOverflow;
        };
    }, [isModalOpen]);

    // Show loading state
    if (loading) {
        return (
            <div className="notification-container">
                <div className="notification-loading">
                    <p>Loading notification logs...</p>
                </div>
            </div>
        );
    }

    // Show error state
    if (error) {
        return (
            <div className="notification-container">
                <div className="notification-error">
                    <p>{error}</p>
                    <button 
                        onClick={() => window.location.reload()} 
                        className="retry-btn"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="notification-container">
            <div className="notification-header">
                <div className="notification-search-filter">
                    <div className="search-wrapper">
                        <input 
                            className="notification-search" 
                            placeholder="Search" 
                            value={searchText}
                            onChange={handleSearchChange}
                        />
                    </div>
                    <select 
                        className="status-filter"
                        value={statusFilter}
                        onChange={handleStatusChange}
                    >
                        <option value="All">Status: All</option>
                        <option value="Sent">Sent</option>
                        <option value="Failed">Failed</option>
                    </select>
                </div>
            </div>
            
            <div className="notification-table-wrapper">
                <table className="notification-table">
                    <thead>
                        <tr>
                            <th>Date / Time</th>
                            <th>Notification</th>
                            <th>Type</th>
                            <th>Status</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentData.map((row) => (
                            <tr key={row.id}>
                                <td>{row.date}</td>
                                <td>{row.notification}</td>
                                <td>{row.type}</td>
                                <td>
                                    <span className={`status-badge ${row.status.toLowerCase()}`}>
                                        {row.status}
                                    </span>
                                </td>
                                <td>
                                    <button 
                                        className="view-content-btn"
                                        onClick={() => handleViewContent(row)}
                                    >
                                        View content
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            
       <div className="notification-pagination">
                    <button className="pagination-btn prev" onClick={goToPreviousPage} disabled={currentPage === 1}><svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
               <path d="M15.8333 10L4.16668 10M4.16668 10L10 15.8334M4.16668 10L10 4.16669" stroke="#FCFCFD" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
               </svg>Previous</button>
                    <span className="pagination-center-text">
                        {totalItems === 0
                            ? 'Showing 0 to 0 of 0 entries'
                            : `Showing ${(currentPage - 1) * itemsPerPage + 1} to ${Math.min(currentPage * itemsPerPage, totalItems)} of ${totalItems} entries`}
                    </span>
                    <button className="pagination-btn next" onClick={goToNextPage} disabled={currentPage === totalPages}>Next<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
             <path d="M4.16675 9.99996H15.8334M15.8334 9.99996L10.0001 4.16663M15.8334 9.99996L10.0001 15.8333" stroke="#FCFCFD" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
             </svg></button>
        </div>

            {/* Notification Content Modal */}
            {isModalOpen && selectedNotification && (
                <dialog
                    ref={dialogRef}
                    className="notification-modal-dialog"
                    aria-labelledby="notification-modal-title"
                    onCancel={handleDialogCancel}
                    onClick={handleDialogClick}
                >
                    <div className="notification-modal-content">
                        <div className="notification-modal-header">
                            <h2 id="notification-modal-title">Alarm Notification</h2>
                            <button className="notification-modal-close" onClick={closeModal}>
                                ×
                            </button>
                        </div>
                        
                        <div className="notification-modal-body">
                            <div className="notification-modal-title">
                                <h3>ShadowIQ: Daily Alarm Notification - {selectedNotification.date}</h3>
                            </div>
                            
                            <div className="notification-modal-details">
                                <div className="notification-detail-item">
                                    <strong>Recipients:</strong> {selectedNotification.recipients.join(', ')}
                                </div>
                                <div className="notification-detail-item">
                                    <strong>Alarm Count:</strong> {selectedNotification.alarmCount}
                                </div>
                                <div className="notification-detail-item">
                                    <strong>Delivery Status:</strong> 
                                    <span className={`status-badge ${selectedNotification.status.toLowerCase()}`}>
                                        {selectedNotification.status}
                                    </span>
                                </div>
                                <div className="notification-detail-item">
                                    <strong>Delivery Format:</strong> {selectedNotification.type}
                                </div>
                            </div>
                            
                            <div className="notification-modal-content-section">
                                <h4>Notification Content:</h4>
                                <div className="notification-content">
                                    {selectedNotification.content ? (
                                        <div dangerouslySetInnerHTML={{ __html: selectedNotification.content }} />
                                    ) : (
                                        <p>No content available for this notification.</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </dialog>
            )}
        </div>
    );
};

export default Notification;
